import { Button, FormattedRelativeTime, InlineStack, Text } from '@customerio/pluma-components/react';

export type NewItemsStatusProps = {
  /** Epoch millis of the last completed check or list load (`0` ⇒ not loaded yet → em dash). */
  lastChecked: number;
  /** True while the check or a "load new" refetch runs. */
  isChecking: boolean;
  /** Runs a check now, instead of waiting for the next interval. */
  onCheckNow: () => void;
  /** Accessible name of the check-now button, e.g. "Check for new activity". */
  checkNowLabel: string;
};

/**
 * "Updated a few seconds ago ↻" — the toolbar status for a "load new" feed (see
 * `utils/new-items.ts`). The time is relative and updates itself; hover shows the full date. The
 * button runs a check now and spins while any check runs.
 */
export function NewItemsStatus({ lastChecked, isChecking, onCheckNow, checkNowLabel }: NewItemsStatusProps) {
  return (
    <InlineStack alignBlock="center" gap="050" data-test-new-items-status={isChecking ? 'checking' : 'idle'}>
      <Text size="sm" color="subtle">
        {/* Keyed by the time: the component caches "now" between its own updates, so a check that
            finishes after that cached time would read "in a few seconds". A remount reads "now" fresh. */}
        Updated <FormattedRelativeTime key={lastChecked} date={lastChecked} shouldUppercaseFirst={false} withTooltip />
      </Text>
      <Button
        aria-label={checkNowLabel}
        icon="reset"
        isIconOnly
        isLoading={isChecking}
        onClick={onCheckNow}
        size="sm"
        variant="tertiary"
      />
    </InlineStack>
  );
}
